import { Form, FormProps, Input, Modal, ModalProps, Typography } from 'antd';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { ButtonListFooterModalSM } from '../components/buttonsList';
import { IBankAccount } from '../../interfaces/bankAccounts';
import Details from '../components/details/details';
import { bankDetailsFields } from '../components/details/details.constants';
import { IBank } from '../../interfaces/banks';
import ICurrency from '../currencies/currency.interface';
import { bankRulesSimple, clearFieldErrorMessageOnChange, currencyRulesSimple } from '../../utils/form.constants';
import {
  abaRoutingNumberRules,
  accountNumberRules,
  bankStateBranchNumberRules,
  branchNumberRules,
  ibanRules,
  isBankAccountFieldOptional,
  isIbanRequiredByCountryAndCurrency,
} from './addAccount.constants';
import { useNotificationOkCancel } from '../../utils/hooks/useNotificationOkCancel.hook';
import { confirmModalUnsavedChangesProps } from '../components/constants';
import { AutoComplete, AutoCompleteBankInput } from '../../components/inputs/autocomplete';
import { Option } from '../../components/inputs/autocomplete/autocomplete';
import { mapCurrencyItem } from '../../utils/edit.constants';
import { DownOutlined } from '@ant-design/icons';

interface IProps {
  formProps: FormProps;
  modalProps: ModalProps;
  formIsLoading: boolean;
  formSubmit: (data: any) => void;
  close: () => void;
  changeBankAccountData: (data: IBankAccount) => void;
  bankData?: IBank | null;
}
export const NewAccountModalCreate = ({
  close,
  modalProps,
  formProps,
  formIsLoading,
  formSubmit,
  changeBankAccountData,
  bankData,
}: IProps) => {
  const [showBankDetails, setShowBankDetails] = useState(false);
  const [currencyData, setCurrencyData] = useState<ICurrency>();

  useEffect(() => {
    formProps.form?.setFieldValue('bankId', bankData?.name);
  }, [bankData, formProps.form, modalProps.visible]);

  const onSubmit = useCallback(
    (formValues: IBankAccount) => {
      changeBankAccountData({
        accountNumber: formValues.accountNumber,
        currencyCode: currencyData?.code ?? '',
        bankId: bankData?.id ?? 0,
        bank: bankData ?? null,
        abaRoutingNumber: formValues.abaRoutingNumber || undefined,
        bankStateBranchNumber: formValues.bankStateBranchNumber || undefined,
        branchNumber: formValues.branchNumber || undefined,
        iban: formValues.iban || undefined,
      });
      formSubmit({});
    },
    [currencyData, bankData, formSubmit, changeBankAccountData],
  );

  const onSave = useCallback(() => {
    formProps.form?.submit();
  }, [formProps]);

  const { openNotification, contextHolder } = useNotificationOkCancel({
    ...confirmModalUnsavedChangesProps,
    onOk: close,
  });

  const onModalCancel = useCallback(() => {
    const formIsNotEmpty =
      formProps.form?.getFieldValue('accountNumber') ?? formProps.form?.getFieldValue('currencyCode');
    if (formIsNotEmpty) {
      openNotification();
    } else {
      close();
    }
  }, [close, formProps.form, openNotification]);

  const bankDetails = (
    <Details
      show={showBankDetails}
      setShow={setShowBankDetails}
      name={'bank'}
      fields={bankDetailsFields}
      data={bankData}
    />
  );

  const onSelectCurrency = (_: ICurrency, { data }: Option<ICurrency>) => {
    setCurrencyData(data);
    formProps.form?.validateFields(['currencyCode']);
  };

  const onClearCurrency = useCallback(() => {
    setCurrencyData(undefined);
  }, []);

  const afterClose = useCallback(() => {
    onClearCurrency();
    formProps.form?.resetFields();
  }, [formProps.form, onClearCurrency]);

  useEffect(() => {
    formProps.form?.resetFields(['abaRoutingNumber', 'bankStateBranchNumber', 'branchNumber', 'iban']);
  }, [bankData, currencyData, formProps.form]);

  const numberField = useMemo(() => {
    return (
      <>
        <Form.Item name='abaRoutingNumber' rules={abaRoutingNumberRules(bankData?.country)}>
          <Input
            placeholder='ABA routing number'
            style={{
              height: '35px',
            }}
            disabled={bankData?.country !== 'US'}
          />
        </Form.Item>
        <div style={{ marginTop: '-16px', display: 'flex', justifyContent: 'start', marginBottom: '16px' }}>
          <Typography.Text
            style={{
              color: '#F28A28',
              fontSize: 12,
              fontWeight: 400,
              lineHeight: '16px',
            }}>
            {
              'If not available, please key in 000000.'
            }
          </Typography.Text>
        </div>
        <Form.Item name='bankStateBranchNumber' rules={bankStateBranchNumberRules(bankData?.country)}>
          <Input
            placeholder='Bank State Branch number'
            style={{
              height: '35px',
            }}
            disabled={bankData?.country !== 'AU'}
          />
        </Form.Item>
        <div style={{ marginTop: '-16px', display: 'flex', justifyContent: 'start', marginBottom: '16px' }}>
          <Typography.Text
            style={{
              color: '#F28A28',
              fontSize: 12,
              fontWeight: 400,
              lineHeight: '16px',
            }}>
            {
              'If not available, please key in 000000.'
            }
          </Typography.Text>
        </div>
        <Form.Item name='branchNumber' rules={branchNumberRules(bankData?.country, currencyData?.code)}>
          <Input
            placeholder='Branch number'
            style={{
              height: '35px',
            }}
            disabled={
              bankData?.country === 'AU' ||
              bankData?.country === 'US' ||
              isBankAccountFieldOptional(bankData?.country, currencyData?.code) ||
              isIbanRequiredByCountryAndCurrency(bankData?.country, currencyData?.code)
            }
          />
        </Form.Item>
        <div style={{ marginTop: '-16px', display: 'flex', justifyContent: 'start', marginBottom: '16px' }}>
          <Typography.Text
            style={{
              color: '#F28A28',
              fontSize: 12,
              fontWeight: 400,
              lineHeight: '16px',
            }}>
            {
              'If not available, please key in 000000.'
            }
          </Typography.Text>
        </div>
      </>
    );
  }, [bankData, currencyData]);

  const ibanField = useMemo(() => {
    return (
      <Form.Item name='iban' rules={ibanRules(bankData?.country, currencyData?.code)}>
        <Input
          placeholder='IBAN'
          style={{
            height: '35px',
          }}
          disabled={!isIbanRequiredByCountryAndCurrency(bankData?.country, currencyData?.code)}
        />
      </Form.Item>
    );
  }, [bankData, currencyData]);

  return (
    <>
      <Modal
        {...modalProps}
        title='Add new account'
        style={{ maxWidth: '400px', textAlign: 'center', marginTop: '100px' }}
        afterClose={afterClose}
        onCancel={onModalCancel}
        footer={ButtonListFooterModalSM(onSave, onModalCancel, formIsLoading)}
      >
        {contextHolder}
        <Form
          {...formProps}
          layout='vertical'
          onFinish={onSubmit}
          disabled={formIsLoading}
          onValuesChange={clearFieldErrorMessageOnChange(formProps.form)}
        >
          <Form.Item name='bankId' rules={bankRulesSimple(bankData)}>
            <AutoCompleteBankInput disabled={true} allowClear refetchOnEmptyValue />
          </Form.Item>
          {bankDetails}
          <Form.Item name='accountNumber' rules={accountNumberRules}>
            <Input
              placeholder='Acc No'
              style={{
                height: '35px',
              }}
            />
          </Form.Item>
          <Form.Item
            name='currencyCode'
            rules={currencyRulesSimple(currencyData)}
            style={{
              width: '100%',
            }}
          >
            <AutoComplete
              allowClear
              resource='api/v1/currencies'
              mapItem={mapCurrencyItem}
              onSelect={onSelectCurrency}
              onClear={onClearCurrency}
            >
              <Input placeholder='Currency' size='large' suffix={<DownOutlined />} />
            </AutoComplete>
          </Form.Item>
          {numberField}
          {ibanField}
        </Form>
      </Modal>
    </>
  );
};
